import { useLatestBidSubmissions } from 'api/elplan/bidSubmissions.api'
import type { BotStrategyConfiguration } from 'api/elplan/elplan.types'
import { useBotStrategySettingQuery } from 'api/elplan/intradayTrading.api'
import {
  countRowsThroughLastValue,
  getPrefillWindow,
  PREFILL_WINDOW_HOURS,
} from '../../../helpers/intradayBidSubmissions.helper'
import { getColumnUnitKey, getConfiguredUnitColumns, parseCellKey } from '../helpers/BidInputTable.helpers'
import type { ConfiguredPosition } from '../helpers/BidInputValidation.helper'
import { mapBotStrategyResponseToRowValues } from '../helpers/BotStrategyTable.helpers'
import { parseStrategyCellKey } from '../tableCellAdapters/strategyTableAdapter'

type PrefilledRowCountInput = {
  systemId: number | undefined
  bidBaseStartTime: Date
  hasBidInput: boolean
  hasBotStrategy: boolean
  configuredPositions: ConfiguredPosition[]
  activeSubtype: string | undefined
  botStrategySeries: BotStrategyConfiguration['series']
}

export function usePrefilledRowCount({
  systemId,
  bidBaseStartTime,
  hasBidInput,
  hasBotStrategy,
  configuredPositions,
  activeSubtype,
  botStrategySeries,
}: PrefilledRowCountInput): number {
  const { startTime, endTime } = getPrefillWindow(bidBaseStartTime)

  const { data: bidRows } = useLatestBidSubmissions(hasBidInput ? (systemId ?? 0) : 0, startTime, endTime)
  const { data: botStrategyResponse } = useBotStrategySettingQuery(
    hasBotStrategy ? systemId : undefined,
    startTime,
    endTime
  )

  const configuredUnitKeys = new Set(getConfiguredUnitColumns(configuredPositions, activeSubtype).map((c) => c.unitKey))
  const isConfiguredCell = (cellKey: string) => {
    const cell = parseCellKey(cellKey)
    return cell !== null && configuredUnitKeys.has(getColumnUnitKey(cell.positionId, cell.tagId))
  }
  const configuredSeriesIds = new Set(botStrategySeries.map((series) => series.id))
  const isConfiguredSeriesCell = (cellKey: string) => {
    const cell = parseStrategyCellKey(cellKey)
    return cell !== null && configuredSeriesIds.has(cell.seriesId)
  }

  const bidRowCount = bidRows ? countRowsThroughLastValue(bidRows, isConfiguredCell) : 0
  const botStrategyRowCount = botStrategyResponse
    ? countRowsThroughLastValue(
        mapBotStrategyResponseToRowValues(botStrategyResponse, bidBaseStartTime, PREFILL_WINDOW_HOURS),
        isConfiguredSeriesCell
      )
    : 0
  return Math.max(bidRowCount, botStrategyRowCount)
}
